// Phase 2/5 — trimmed "New student / client" create modal. Reachable by ALL staff from the global
// ☰ menu (Phase 2) and inline from the booking person picker (Phase 5, pre-filled with the typed
// text). Role locked to student | client; only the non-privileged identity fields. On create the
// server issues a one-time temp password (shown once). Phase 5: an advisory name-duplicate (409
// NAME_DUPLICATE) prompts "is this the same person?" — the operator picks the existing match (if a
// picker handler is supplied) or creates a distinct record (confirm_new). Creation is online-only.

import { useState, type CSSProperties } from 'react';
import { C, F, Modal, Btn, ErrorText } from './adminUi';
import { createScopedUser, type DuplicateMatch } from '../../lib/admin';
import { NetworkError } from '../../lib/api';

const ROLE_OPTIONS = [
  { value: 'student', label: 'Student' },
  { value: 'client', label: 'Client' },
] as const;
const inp: CSSProperties = { fontFamily: F, fontSize: 13, padding: '6px 8px', border: `1px solid ${C.borderMed}`, borderRadius: 6, width: '100%' };
const lbl: CSSProperties = { fontWeight: 600, fontSize: 12, color: C.textDim, display: 'block', marginBottom: 3 };

interface Props {
  onClose: () => void;
  // Called after a successful create. `created` (when present) lets the booking flow add the new
  // person immediately (id + role → capacity). Callers that don't need it (the ☰ menu) ignore it.
  onSaved: (created?: { id: string; role: 'student' | 'client'; label: string }) => void;
  prefill?: { first_name?: string; last_name?: string };
  // When a name-duplicate is found, lets the operator pick the EXISTING person instead of creating.
  onPickExisting?: (id: string) => void;
}

export default function StudentClientCreateModal({ onClose, onSaved, prefill, onPickExisting }: Props) {
  const [v, setV] = useState<Record<string, string | boolean>>({
    role: 'student', arn_pending: false,
    first_name: prefill?.first_name ?? '', last_name: prefill?.last_name ?? '',
  });
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [tempPw, setTempPw] = useState<string | null>(null);
  const [dupes, setDupes] = useState<DuplicateMatch[] | null>(null);
  const [created, setCreated] = useState<{ id: string; role: 'student' | 'client'; label: string } | null>(null);
  const [touched, setTouched] = useState(false);

  const set = (k: string, val: string | boolean) => { setTouched(true); setV((s) => ({ ...s, [k]: val })); };
  const str = (k: string) => (typeof v[k] === 'string' ? (v[k] as string) : '');
  const isStudent = v.role === 'student';
  const role = (v.role === 'client' ? 'client' : 'student') as 'student' | 'client';

  async function submit(confirmNew = false) {
    setError(null); setDupes(null);
    const first = str('first_name').trim();
    const last = str('last_name').trim();
    const company = str('company').trim();
    if (!((first && last) || company)) { setError('Enter a first and last name, or a company.'); return; }
    const body: Record<string, unknown> = {
      role: v.role,
      ...(first ? { first_name: first } : {}),
      ...(last ? { last_name: last } : {}),
      ...(company ? { company } : {}),
      email: str('email'),
      ...(str('mobile') ? { mobile: str('mobile') } : {}),
      ...(isStudent && str('training_stage') ? { training_stage: str('training_stage') } : {}),
      ...(isStudent && str('arn') ? { arn: str('arn') } : {}),
      ...(isStudent ? { arn_pending: !!v.arn_pending } : {}),
      ...(confirmNew ? { confirm_new: true } : {}),
    };
    setBusy(true);
    try {
      const res = await createScopedUser(body);
      setBusy(false);
      if (res.duplicates && res.duplicates.length) { setDupes(res.duplicates); return; } // advisory — confirm
      if (res.error || !res.data) { setError(res.error ?? 'Could not create.'); return; }
      // Booking-facing label for the new person (mirrors personDisplayLabel).
      const name = first && last ? `${first} ${last}` : company;
      const label = company && name !== company ? `${name} · ${company}` : name;
      setCreated({ id: res.data.user.id, role, label });
      setTempPw(res.data.temp_password);
    } catch (e) {
      setBusy(false);
      // Creation is online-only — no offline queue (5d model). Surface a clear message.
      setError(e instanceof NetworkError ? 'Creating a person needs a connection — you appear to be offline.' : 'Could not create. Please try again.');
    }
  }

  if (tempPw) {
    const done = () => onSaved(created ?? undefined);
    return (
      <Modal title={isStudent ? 'Student created' : 'Client created'} onClose={done} width={460}>
        <p style={{ fontSize: 13, color: C.textDim }}>The account is created. Give this <b>one-time temporary password</b> to the person out-of-band (no email is sent yet). They should change it on first login.</p>
        <div style={{ fontFamily: 'monospace', fontSize: 16, background: C.surfaceAlt, border: `1px solid ${C.borderMed}`, borderRadius: 8, padding: '12px 14px', textAlign: 'center', letterSpacing: 1, margin: '12px 0' }}>{tempPw}</div>
        <Btn kind="primary" onClick={done}>Done</Btn>
      </Modal>
    );
  }

  if (dupes) {
    return (
      <Modal title="Possible duplicate" onClose={() => setDupes(null)} width={460}>
        <p style={{ fontSize: 13, color: C.textDim }}>A user of this name already exists — is this the same person?</p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, margin: '10px 0' }}>
          {dupes.map((d) => (
            <div key={d.id} style={{ display: 'flex', alignItems: 'center', gap: 8, background: C.surfaceAlt, border: `1px solid ${C.borderMed}`, borderRadius: 8, padding: '8px 10px' }}>
              <span style={{ flex: 1, fontSize: 13 }}>{d.label}</span>
              {onPickExisting && <Btn kind="ghost" onClick={() => onPickExisting(d.id)}>Use this person</Btn>}
            </div>
          ))}
        </div>
        {error && <ErrorText>{error}</ErrorText>}
        <div style={{ display: 'flex', gap: 10, marginTop: 12, paddingTop: 12, borderTop: `1px solid ${C.border}` }}>
          <Btn kind="primary" disabled={busy} onClick={() => submit(true)}>{busy ? 'Creating…' : 'Create as a new record'}</Btn>
          <Btn kind="ghost" onClick={() => setDupes(null)}>Back</Btn>
        </div>
      </Modal>
    );
  }

  return (
    <Modal title="New student / client" onClose={onClose} width={520} dirty={touched}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
        <div><label style={lbl}>Role *</label>
          <select value={String(v.role)} onChange={(e) => set('role', e.target.value)} style={inp}>
            {ROLE_OPTIONS.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
          </select>
        </div>
        <div><label style={lbl}>Mobile</label><input style={inp} value={str('mobile')} onChange={(e) => set('mobile', e.target.value)} /></div>
        <div><label style={lbl}>First name</label><input style={inp} value={str('first_name')} onChange={(e) => set('first_name', e.target.value)} /></div>
        <div><label style={lbl}>Last name</label><input style={inp} value={str('last_name')} onChange={(e) => set('last_name', e.target.value)} /></div>
        <div style={{ gridColumn: '1 / -1' }}><label style={lbl}>Company</label><input style={inp} value={str('company')} onChange={(e) => set('company', e.target.value)} placeholder={isStudent ? '(optional)' : 'or a person name above'} /></div>
        <div style={{ gridColumn: '1 / -1', fontSize: 11.5, color: C.textMuted, marginTop: -4 }}>Enter a first and last name, or a company.</div>
        <div><label style={lbl}>Email *</label><input style={inp} value={str('email')} onChange={(e) => set('email', e.target.value)} /></div>
        {isStudent && <>
          <div><label style={lbl}>Training stage</label><input style={inp} value={str('training_stage')} onChange={(e) => set('training_stage', e.target.value)} /></div>
          <div><label style={lbl}>ARN</label><input style={inp} value={str('arn')} onChange={(e) => set('arn', e.target.value)} /></div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}><input type="checkbox" checked={!!v.arn_pending} onChange={(e) => set('arn_pending', e.target.checked)} style={{ width: 16, height: 16, accentColor: C.accent }} /><span style={{ fontSize: 12.5, color: C.textDim }}>ARN pending</span></div>
        </>}
      </div>
      {error && <ErrorText>{error}</ErrorText>}
      <div style={{ display: 'flex', gap: 10, marginTop: 14, paddingTop: 12, borderTop: `1px solid ${C.border}` }}>
        <Btn kind="primary" disabled={busy} onClick={() => submit(false)}>{busy ? 'Creating…' : 'Create'}</Btn>
        <Btn kind="ghost" onClick={onClose}>Cancel</Btn>
      </div>
    </Modal>
  );
}
